import React, { useState, useEffect } from 'react';
import Slider from 'react-slick';
import BannerService from '../../services/BannerService';
import './BannerCarousel.css';
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";

const BannerCarousel = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mediaLoaded, setMediaLoaded] = useState({});

  // Helper function to check if URL is a video
  const isVideoUrl = (url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
    const urlLower = url.toLowerCase();
    return videoExtensions.some(ext => urlLower.includes(ext)) || 
           urlLower.includes('video');
  };

  // Handle media load completion
  const handleMediaLoad = (bannerId) => {
    setMediaLoaded(prev => ({ ...prev, [bannerId]: true }));
  };

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        console.log('[BannerCarousel] Starting to fetch banners...');
        const bannerData = await BannerService.getBanners();
        console.log('[BannerCarousel] Received banner data:', bannerData);
        console.log('[BannerCarousel] Number of banners:', bannerData?.length || 0);
        setBanners(bannerData || []);
        setError(null);
      } catch (error) {
        console.error('[BannerCarousel] Failed to fetch banners:', error);
        setError(error.message);
        setBanners([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  // Default banner if no banners are available
  const defaultBanner = {
    id: 'default',
    title: 'Welcome to GG Trophy',
    description: 'Earn rewards, redeem prizes, and level up your gaming experience!',
    imageUrl: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=1920&h=1080&fit=crop',
    link: null
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    fade: true,
    cssEase: 'linear',
    pauseOnHover: true
  };

  if (loading) {
    return (
      <div className="banner-carousel">
        <div className="banner-slide banner-loading">
          <div className="banner-content">
            <h1>Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  const displayBanners = banners.length > 0 ? banners : [defaultBanner];

  console.log('[BannerCarousel] Rendering with banners:', displayBanners);
  console.log('[BannerCarousel] Loading state:', loading);
  console.log('[BannerCarousel] Error state:', error);

  return (
    <div className="banner-carousel">
      {error && (
        <div style={{ 
          position: 'absolute', 
          top: '10px', 
          right: '10px', 
          background: 'rgba(255,0,0,0.7)', 
          color: 'white', 
          padding: '10px', 
          borderRadius: '5px',
          zIndex: 1000,
          fontSize: '12px'
        }}>
          Error: {error}
        </div>
      )}
      <Slider {...settings}>
        {displayBanners.map((banner, index) => {
          const isVideo = isVideoUrl(banner.imageUrl);
          
          const bannerId = banner.id || index;
          const isMediaLoaded = mediaLoaded[bannerId];
          
          return (
            <div key={bannerId}>
              <div 
                className="banner-slide" 
                style={{
                  backgroundImage: !isVideo || !isMediaLoaded 
                    ? `url(${defaultBanner.imageUrl})` 
                    : 'none'
                }}
              >
                {isVideo ? (
                  <video
                    className="banner-video"
                    src={banner.imageUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{ opacity: isMediaLoaded ? 1 : 0, transition: 'opacity 0.5s' }}
                    onLoadedData={() => handleMediaLoad(bannerId)}
                    onError={(e) => {
                      console.error('[BannerCarousel] Video failed to load:', banner.imageUrl, e);
                      console.error('[BannerCarousel] Video element:', e.target);
                      // Keep showing default banner if video fails
                    }}
                  />
                ) : (
                  <img
                    src={banner.imageUrl}
                    alt={banner.title}
                    style={{ display: 'none' }}
                    onLoad={() => handleMediaLoad(bannerId)}
                    onError={(e) => {
                      console.error('[BannerCarousel] Image failed to load:', banner.imageUrl, e);
                    }}
                  />
                )}
                <div className="banner-overlay"></div>
                <div className="banner-content-wrapper">
                  <div className="banner-content">
                    {/* <div className="banner-tag">Featured</div> */}
                    <h1 className="banner-title">{banner.title}</h1>
                    <p className="banner-description">{banner.description}</p>
                    {banner.link && (
                      <div className="banner-actions">
                        <a href={banner.link} className="banner-link primary" target="_blank" rel="noopener noreferrer">
                          <span>Learn More</span>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                            <polyline points="12 5 19 12 12 19"></polyline>
                          </svg>
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </Slider>
    </div>
  );
};

export default BannerCarousel;
